import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { fetchUsersList } from "./store";
import { ComponentLoader } from '../common/Loader';
import Swal from "sweetalert2";
import { UserRound, Info, CircleUserRound, X, ChevronUp, ChevronDown } from 'lucide-react';

const DetailItem = ({ label, value }) => {
    let displayValue = 'N/A';
    if (value !== null && value !== undefined && value !== '') {
        if (typeof value === 'string') {
            displayValue = value
                .split('_')
                .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1) : '')
                .join(' ');
        } else {
            displayValue = value;
        }
    }
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
            <span style={{ fontSize: '1rem', color: '#111827', fontWeight: '500', wordBreak: 'break-all', marginTop: '4px' }}>{displayValue}</span>
        </div>
    );
};

const AllUsersTable = ({ filters, startDate, endDate, onLoadingChange }) => {
    const dispatch = useDispatch();
    const [users, setUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortOption, setSortOption] = useState('createdDate:desc');
    const [querySort, setQuerySort] = useState({ sortBy: 'createdDate', sortOrder: 'desc' });
    const [goToPage, setGoToPage] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const lastFetchParams = React.useRef(null);
    const isFetching = React.useRef(false);

    useEffect(() => {
        const currentParams = JSON.stringify({ filters, startDate, endDate, currentPage, itemsPerPage, querySort });
        const cacheKey = `ALL_USERS_TABLE_CACHE_${currentParams}`;
        let cacheExists = false;

        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
            const data = JSON.parse(cachedData);
            if (data === 'PENDING') {
                    
                return;
            }

            if (data && typeof data === 'object') {
                const cacheTimestamp = data.cacheTimestamp || 0;
                const threeMinutes = 3 * 60 * 1000;
                const isExpired = Date.now() - cacheTimestamp > threeMinutes;
                if (!isExpired) {
                    
                    setUsers(data.users);
                    setTotalCount(data.totalCount);
                    setLoading(false);
                    onLoadingChange(false);
                    lastFetchParams.current = currentParams;
                    return;
                } else {
                    
                    setUsers(data.users);
                    setTotalCount(data.totalCount);
                    setLoading(false);
                    onLoadingChange(false);
                    cacheExists = true;
                } 
            }
        }
        if (lastFetchParams.current === currentParams && !cacheExists) return;
        if (isFetching.current) return;

        const loadData = async () => {
            isFetching.current = true;
            if (!cacheExists) {
                setLoading(true);
                onLoadingChange(true);
            }
            sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));
            
            try {
                const res = await dispatch(fetchUsersList({
                    ...filters,
                    startDate,
                    endDate,
                    page: currentPage,
                    limit: itemsPerPage,
                    ...querySort
                }));
                if (res.payload && res.payload.success) {
                    setUsers(res.payload.data);
                    setTotalCount(res.payload.totalCount);
                    sessionStorage.setItem(cacheKey, JSON.stringify({ 
                        users: res.payload.data, 
                        totalCount: res.payload.totalCount,
                        cacheTimestamp: Date.now()
                    }));
                    lastFetchParams.current = currentParams;
                } else {
                    sessionStorage.removeItem(cacheKey);
                }
            } catch (error) {
                console.error("Error fetching users list:", error);
                sessionStorage.removeItem(cacheKey);
            } finally {
                setLoading(false);
                onLoadingChange(false);
                isFetching.current = false;
            }
        };
        loadData();
    }, [dispatch, filters, startDate, endDate, currentPage, itemsPerPage, querySort, onLoadingChange]);

    const handleSortChange = React.useCallback((value) => {
        setSortOption(value);
        setCurrentPage(1);
        const [sortBy, sortOrder] = value.split(':');
        setQuerySort({ sortBy, sortOrder });
    }, []);

    const renderSortHeader = (label, field, customClassName) => (
        <th className={`${customClassName || `th-${field}`} sticky-header-th`} style={{ color: '#2c3e50' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
                <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', gap: '4px' }}>
                    <ChevronUp size={16} strokeWidth={3} color={sortOption === `${field}:asc` ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSortChange(`${field}:asc`)} />
                    <ChevronDown size={16} strokeWidth={3} color={sortOption === `${field}:desc` ? '#0c0c0cff' : '#757373ff'} onClick={() => handleSortChange(`${field}:desc`)} />
                </span>
            </div>
        </th>
    );

    const calculateAge = React.useCallback((ageValue) => {
        if (ageValue !== undefined && ageValue !== null && ageValue !== "" && !isNaN(Number(ageValue))) {
            return Number(ageValue);
        }
        return "N/A";
    }, []);

    const normalizeGender = React.useCallback((gender) => {
        if (!gender) return { display: "—", className: "" };
        const g = String(gender).toLowerCase().trim();
        if (g === 'male' || g === 'm') return { display: "Male", className: "active" };
        if (g === 'female' || g === 'f') return { display: "Female", className: "inactive" };
        return { display: gender, className: "inactive" };
    }, []);

    const formatDateTime = React.useCallback((dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return null;

        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        let hours = date.getHours();
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const strHours = String(hours).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return (
            <>
                {month} {day} {year}
                <br />
                {strHours}.{minutes}.{seconds} {ampm}
            </>
        );
    }, []);

    const handleGoToPage = () => {
        const totalPages = Math.ceil(totalCount / itemsPerPage);
        const pageNum = parseInt(goToPage, 10);
        if (!pageNum || pageNum < 1 || pageNum > totalPages) {
            Swal.fire('Invalid Page', `Total pages: ${totalPages}`, 'warning');
            return;
        }
        setCurrentPage(pageNum);
        setGoToPage('');
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    return (
        <div className="user-directory-container mt-4" style={{ position: 'relative', zIndex: 5, overflow: 'visible' }}>
            <style>{`
                .sticky-table { border-collapse: separate !important; border-spacing: 0 !important; overflow: visible !important; position: relative !important; }
                .sticky-header-th { position: sticky !important; top: -1px !important; z-index: 11 !important; background: #e3e7e7ff !important; border-bottom: 1px solid #e5e7eb !important; padding: 14px 16px !important; font-size: 13.5px !important; font-weight: 650 !important; color: #2c3e50 !important; text-align: center !important; text-transform: uppercase !important; letter-spacing: 0.5px !important; }
                .scroll-container { max-height: 600px; overflow-y: auto !important; overflow-x: auto !important; position: relative !important; }
            `}</style>
            <div className="directory-header d-flex justify-content-between align-items-center" style={{ padding: '24px 30px' }}>
                <div>
                    <h4 className="directory-title">Users Details</h4>
                    <p className="directory-subtitle">Manage and view users</p>
                </div>
                <div className="d-flex" style={{ gap: '15px' }}>
                    <span className="total-count-badge">Totals: {totalCount}</span>
                </div>
            </div>

            <div className="modern-table-wrapper scroll-container">
                <table className="modern-table sticky-table">
                    <thead className="modern-table-header" style={{ background: '#e3e7e7ff' }}>
                        <tr>
                            <th className="th-index sticky-header-th" style={{ color: '#2c3e50' }}>#</th>
                            <th className="th-user-info sticky-header-th" style={{ color: '#2c3e50' }}>USER ID</th>
                            {renderSortHeader('AGE/GENDER', 'age', 'th-age')}
                            <th className="th-device sticky-header-th" style={{ color: '#2c3e50' }}>DEVICE</th>
                            <th className="th-location sticky-header-th" style={{ color: '#2c3e50' }}>LOCATION</th>
                            {renderSortHeader('CREATED DATE', 'createdDate')}
                            <th className="th-profileStatus sticky-header-th" style={{ color: '#2c3e50' }}>PROFILE STATUS</th>
                            <th className="th-status sticky-header-th" style={{ color: '#2c3e50' }}>STATUS</th>
                            {renderSortHeader('LAST ACTIVE', 'last_active', 'th-lastactive')}
                            <th className="th-status sticky-header-th" style={{ color: '#2c3e50' }}>INFO</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan="10" className="loading-cell">
                                    <div>
                                        <div className="loading-spinner"></div>
                                        <span>Loading users...</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!loading && users.length > 0 ? users.map((user, index) => {
                            const genderInfo = normalizeGender(user.profile?.gender || user.gender);
                            return (
                                <tr key={user._id} className="modern-table-row">
                                    <td className="td-index">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td className="td-user-info">
                                        <div className="user-info-cell">
                                            <span className="user-id">{user._id}</span>
                                        </div>
                                    </td>
                                    <td className="td-age-gender">
                                        {calculateAge(user.profile?.age ?? user.age)} / <span className={genderInfo.className}>{genderInfo.display}</span>
                                    </td>
                                    <td className="td-device">
                                        <div className="device-cell">
                                            {user.deviceType === 'A' ? 'Android' : user.deviceType === 'I' || user.deviceType === 'iOS' ? 'iOS' : '—'}
                                        </div>
                                    </td>
                                    <td className="td-location">
                                        {[user.ipData?.location?.city, user.ipData?.location?.region?.name, user.ipData?.location?.country?.name].filter(Boolean).join(", ") || "—"}
                                    </td>
                                    <td className="td-CreatedDate">{user.createdDate ? new Date(user.createdDate).toLocaleDateString() : "—"}</td>
                                    <td className="td-profileStatus">
                                        <span style={{ fontWeight: '600', fontSize: '12px', color: user.userDataFlag === 'A' ? '#10b981' : '#ef4444' }}>
                                            {user.userDataFlag === 'A' ? 'Completed' : 'Incomplete'}
                                        </span>
                                    </td>
                                    <td className="td-status">
                                        <span className={`status-badge ${user.loginStatus === 'A' ? 'active' : 'inactive'}`}>
                                            {user.loginStatus === 'A' ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className='td-lastactive'>
                                        {formatDateTime(user.last_active || user.modifiedDate) || "—"}
                                    </td>
                                    <td className="td-info">
                                        <button className="info-btn" onClick={() => { setSelectedUser(user); setIsModalOpen(true); }}>
                                            <Info size={18} />
                                            <span className="tooltip">User Info</span>
                                        </button>
                                    </td>
                                </tr>
                            );
                        }) : !loading && (
                            <tr>
                                <td colSpan="10" className="no-data-cell">
                                    <div className="no-data-message">
                                        <p>No user data found</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pq-pagination-container d-flex justify-content-between align-items-center" style={{ padding: '16px 24px', background: '#fff', borderTop: '1px solid #f1f5f9' }}>
                <div className="d-flex align-items-center">
                    <span className="pq-pagination-text me-2" style={{ fontSize: '14px', color: '#64748b', fontWeight: '600' }}>
                        Showing {totalCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} – {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries. Per page: 
                    </span>
                    <select 
                        value={itemsPerPage} 
                        onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} 
                        className="form-select form-select-sm" 
                        style={{ width: '70px', display: 'inline-block', height: '32px', padding: '0 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>

                <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item pq-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link pq-page-link" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>
                            &laquo;
                        </button>
                    </li>

                    {[...Array(Math.ceil(totalCount / itemsPerPage) || 1)].map((_, i) => {
                        const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
                        if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                            return (
                                <li className={`page-item pq-page-item ${i + 1 === currentPage ? 'active' : ''}`} key={i}>
                                    <button className="page-link pq-page-link" onClick={() => setCurrentPage(i + 1)}>
                                        {i + 1}
                                    </button>
                                </li>
                            );
                        }
                        if (i === 1 && currentPage > 3) return <li className="page-item pq-page-item disabled" key="start-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                        if (i === totalPages - 2 && currentPage < totalPages - 2) return <li className="page-item pq-page-item disabled" key="end-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                        return null;
                    })}

                    <li className={`page-item pq-page-item ${currentPage === (Math.ceil(totalCount / itemsPerPage) || 1) ? 'disabled' : ''}`}>
                        <button className="page-link pq-page-link" onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === (Math.ceil(totalCount / itemsPerPage) || 1)}>
                            &raquo;
                        </button>
                    </li>
                </ul>
            </div>

            {/* User Details Modal */}
            {isModalOpen && selectedUser && ReactDOM.createPortal(
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(4px)', padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff', borderRadius: '16px', width: '100%', maxWidth: '700px',
                        maxHeight: '90vh', display: 'flex', flexDirection: 'column', 
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden'
                    }}>
                        <div style={{ 
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                            padding: '24px', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f8fafc' 
                        }}>
                            <div>
                                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem', fontWeight: '700' }}>User Details</h3>
                                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.875rem' }}>Viewing complete profile information</p>
                            </div>
                            <button onClick={closeModal} style={{
                                background: '#f1f5f9', border: 'none', cursor: 'pointer', padding: '8px', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
                                transition: 'all 0.2s ease', color: '#64748b'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div style={{ 
                            padding: '24px', overflowY: 'auto', 
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' 
                        }}>
                            <DetailItem label="Gender" value={selectedUser.profile?.gender || selectedUser.gender} />
                            <DetailItem label="Age" value={calculateAge(selectedUser.profile?.age ?? selectedUser.age)} />
                            <DetailItem label="Marital Status" value={selectedUser.profile?.maritalStatus || selectedUser.maritalStatus} />
                            <DetailItem label="Profession" value={selectedUser.profile?.profession || selectedUser.profession} />
                            <DetailItem label="Location" value={[selectedUser.ipData?.location?.postal, selectedUser.ipData?.location?.city, selectedUser.ipData?.location?.region?.name, selectedUser.ipData?.location?.country?.name].filter(Boolean).join(", ")} />
                            <DetailItem label="Height" value={(selectedUser.profile?.height || selectedUser.height) ? `${selectedUser.profile?.height || selectedUser.height} cm` : null} />
                            <DetailItem label="Weight" value={(selectedUser.profile?.weight || selectedUser.weight) ? `${selectedUser.profile?.weight || selectedUser.weight} kg` : null} />
                            <DetailItem label="BMI" value={selectedUser.profile?.bmi || selectedUser.bmi} />
                            <DetailItem label="Blood Group" value={selectedUser.profile?.bloodGroup || selectedUser.bloodGroup} />
                            <DetailItem label="Device Type" value={selectedUser.deviceType === 'A' ? 'Android' : selectedUser.deviceType === 'I' || "iOS" ? 'iOS' : selectedUser.deviceType} />
                            <DetailItem label="FCM Token" value={selectedUser.fcmtoken} />
                            <DetailItem label="Profile Status" value={selectedUser.userDataFlag === 'A' ? 'Completed' : 'Incomplete'} />
                            <DetailItem label="Active Status" value={selectedUser.loginStatus === 'A' ? 'Active' : 'Inactive'} />
                            <DetailItem label="App Version" value={selectedUser.appVersion} />
                            <DetailItem label="Last Active" value={selectedUser.last_active} />
                        </div>
                        
                        <div style={{ 
                            padding: '16px 24px', borderTop: '1px solid #f3f4f6', 
                            backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'flex-end' 
                        }}>
                            <button onClick={closeModal} style={{
                                padding: '8px 24px', backgroundColor: '#3b82f6', color: 'white',
                                border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer',
                                transition: 'background-color 0.2s', fontSize: '0.875rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default AllUsersTable;
